import React, { useState, useEffect, useCallback } from 'react';
import { isDemoMode } from '../utils/demoMode';
// Import listClients, deleteClient, and searchClients services
import { listClients, deleteClient, searchClients } from '../services/clientService';
// Added FaSearch and FaTimes icons
import { FaPlus, FaEdit, FaTrashAlt, FaSearch, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { terminology } from '../config/terminology'; // Import terminology
// Import debounce from lodash
import { debounce } from 'lodash';
// Import the modal component
import ConfirmationModal from '../components/ConfirmationModal';
import { Button } from '../components/forms';

const PAGE_SIZE = 20; // Default page size for listing clients

const ClientsPage = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // For fetch errors
  const [apiError, setApiError] = useState(null); // For action errors (like delete)
  const [infoMessage, setInfoMessage] = useState(null); // For informational messages
  const navigate = useNavigate();
  const location = useLocation();
  const [successMessage, setSuccessMessage] = useState(location.state?.message || null);

  // State for confirmation modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  // --- Search State ---
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // --- End Search State ---

  // --- Pagination State ---
  const [nextPageDocId, setNextPageDocId] = useState(null);
  const [pageTokens, setPageTokens] = useState([null]); // Array of page tokens, starting with null for first page
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  // --- End Pagination State ---

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (term, resetPagination = true) => {
      if (term.length === 0 || term.length >= 3) {
        setLoading(true);
        setError(null);
        setApiError(null);
        setInfoMessage(null);
        setIsSearching(term.length >= 3);
        
        // Reset pagination when starting a new search
        if (resetPagination) {
          setCurrentPage(1);
          setNextPageDocId(null);
          setPageTokens([null]);
          setHasPreviousPage(false);
        }
        
        try {
          let data;
          if (term.length >= 3) {
            // Use searchClients if term is 3+ chars
            data = await searchClients({ searchString: term, pageSize: PAGE_SIZE }); // Use same page size as listing
          } else {
            // Use listClients if term is empty (show all)
            data = await listClients({ pageSize: PAGE_SIZE }); // Use PAGE_SIZE for consistency
          }
          setClients(data.clients || []);
          setNextPageDocId(data.nextPageDocId || null);
          setHasNextPage(!!data.nextPageDocId);
        } catch (err) {
          setError(err.message || `Failed to load ${terminology.client.plural.toLowerCase()}.`);
          setClients([]);
          setNextPageDocId(null);
          setHasNextPage(false);
        } finally {
          setLoading(false);
          setIsSearching(false);
        }
      } else {
        // If term is 1 or 2 chars, don't search
        setClients([]);
        setError(null);
        setApiError(null);
        setInfoMessage(null);
        setLoading(false);
        setIsSearching(false);
        setNextPageDocId(null);
        setHasNextPage(false);
        setCurrentPage(1);
        setPageTokens([null]);
        setHasPreviousPage(false);
      }
    }, 500), // 500ms debounce delay
    []
  );

  // Effect to trigger search when searchTerm changes
  useEffect(() => {
    debouncedSearch(searchTerm, true); // Always reset pagination when search term changes
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchTerm, debouncedSearch]);

  // Function to fetch clients (now primarily used for delete refresh)
  const fetchClients = async (startAfterDocId = null, resetPagination = true) => {
    // Only fetch all if search term is empty
    if (searchTerm === '') {
        setLoading(true);
        setError(null);
        setApiError(null);
        setInfoMessage(null);
        
        if (resetPagination) {
          setCurrentPage(1);
          setNextPageDocId(null);
          setPageTokens([null]);
          setHasPreviousPage(false);
        }
        
        try {
          const data = await listClients({ 
            pageSize: PAGE_SIZE,
            startAfterDocId: startAfterDocId 
          });
          setClients(data.clients || []);
          setNextPageDocId(data.nextPageDocId || null);
          setHasNextPage(!!data.nextPageDocId);
        } catch (err) {
          setError(err.message || `Failed to load ${terminology.client.plural.toLowerCase()}.`);
          setClients([]);
          setNextPageDocId(null);
          setHasNextPage(false);
        } finally {
          setLoading(false);
        }
    } else {
        // If there's a search term, let the debounced search handle it
        debouncedSearch(searchTerm, resetPagination);
    }
  };

  useEffect(() => {
    // Clear success/error messages
    let timer;
    if (successMessage || apiError || infoMessage) {
      timer = setTimeout(() => {
        setSuccessMessage(null);
        setApiError(null);
        setInfoMessage(null);
        if (successMessage && location.state?.message) {
            navigate(location.pathname, { replace: true, state: {} });
        }
      }, 3000); // Reduced to 3 seconds for pagination messages
    }
    return () => clearTimeout(timer);
  }, [successMessage, apiError, infoMessage, navigate, location.pathname, location.state?.message]);

  useEffect(() => {
    fetchClients(null, true); // Reset pagination on initial load
  }, []);

  // Pagination functions
  const handleNextPage = async () => {
    if (!hasNextPage || !nextPageDocId) return;
    
    setLoading(true);
    setError(null);
    setApiError(null);
    setInfoMessage(null);
    
    try {
      let data;
      if (searchTerm && searchTerm.length >= 3) {
        data = await searchClients({ 
          searchString: searchTerm, 
          pageSize: PAGE_SIZE,
          startAfterDocId: nextPageDocId 
        });
      } else {
        data = await listClients({ 
          pageSize: PAGE_SIZE,
          startAfterDocId: nextPageDocId 
        });
      }
      
      // Check if the next page has any results
      if (!data.clients || data.clients.length === 0) {
        // No results on next page, stay on current page and show message
        // Also update hasNextPage to false since there are actually no more pages
        setHasNextPage(false);
        setInfoMessage("No more records found.");
        return;
      }
      
      // Add current nextPageDocId to pageTokens for future navigation
      const newPageTokens = [...pageTokens, nextPageDocId];
      
      setClients(data.clients);
      setNextPageDocId(data.nextPageDocId || null);
      setHasNextPage(!!data.nextPageDocId);
      setPageTokens(newPageTokens);
      setCurrentPage(prev => prev + 1);
      setHasPreviousPage(true);
    } catch (err) {
      setError(err.message || `Failed to load next page.`);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousPage = async () => {
    if (!hasPreviousPage || currentPage <= 1) return;
    
    setLoading(true);
    setError(null);
    setApiError(null);
    setInfoMessage(null);
    
    try {
      // Get the token for the previous page
      const newPageTokens = [...pageTokens];
      newPageTokens.pop(); // Remove the current page token
      const prevPageToken = newPageTokens[newPageTokens.length - 1] || null;
      
      let data;
      if (searchTerm && searchTerm.length >= 3) {
        data = await searchClients({ 
          searchString: searchTerm, 
          pageSize: PAGE_SIZE,
          startAfterDocId: prevPageToken 
        });
      } else {
        data = await listClients({ 
          pageSize: PAGE_SIZE,
          startAfterDocId: prevPageToken 
        });
      }
      
      setClients(data.clients || []);
      setNextPageDocId(data.nextPageDocId || null);
      setHasNextPage(!!data.nextPageDocId);
      setPageTokens(newPageTokens);
      setCurrentPage(prev => prev - 1);
      setHasPreviousPage(currentPage > 2);
    } catch (err) {
      setError(err.message || `Failed to load previous page.`);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClient = () => {
    navigate('/clients/add');
  };

  const handleEdit = (id) => {
    navigate(`/clients/edit/${id}`);
  };

  // Updated handleDelete to open the modal
  const handleDelete = (client) => {
    setClientToDelete(client);
    setIsModalOpen(true);
    setApiError(null); // Clear previous errors when opening modal
    setInfoMessage(null); // Clear previous info messages when opening modal
  };

  // Function to close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setClientToDelete(null);
  };

  // Function to confirm and execute deletion
  const confirmDelete = async () => {
    if (!clientToDelete) return;

    try {
      await deleteClient(clientToDelete.id);
      closeModal();
      const displayName = getDisplayName(clientToDelete);
      setSuccessMessage(`${terminology.client.singular} '${displayName}' deleted successfully.`); // Use terminology
      // Refresh the list after deletion, maintaining current page if possible
      const currentPageToken = pageTokens[currentPage - 1] || null;
      fetchClients(currentPageToken, false);
    } catch (err) {
      setApiError(err.message || `Failed to delete ${terminology.client.singular.toLowerCase()}.`); // Use terminology
      closeModal(); // Close modal even on error
    }
  };

  // Handler for search input change
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  // Handler to clear search
  const clearSearch = () => {
    setSearchTerm('');
  };

  const getDisplayName = (client) => {
    return client.clientName || client.businessName || `${client.firstName || ''} ${client.lastName || ''}`.trim() || 'N/A';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 font-sans">
       {/* Title and Back Button Row */}
       <div className="flex justify-between items-center mb-2"><h1 className="text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">{terminology.client.plural}</h1><Link to="/" className="text-gray-500 hover:text-gray-900 font-medium transition-colors">
             Back to Dashboard
         </Link></div>

       {/* Buttons and Search Row */}
       <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            {/* Add Button */}
            {!isDemoMode() && (
            <button
                onClick={handleAddClient}
                className="bg-green-600 hover:bg-green-500 text-white font-semibold py-2 px-5 rounded-full shadow-sm inline-flex items-center transition duration-300"
            ><FaPlus className="mr-2" />
                Add {terminology.client.singular}
            </button>
            )}

            {/* Search Input */}
            <div className="relative flex-grow sm:flex-grow-0 sm:w-64 md:w-72"><input
                    type="text"
                    placeholder={`Search ${terminology.client.plural.toLowerCase()}...`}
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="block rounded-full border-0 py-2.5 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6 pl-10 w-full focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                /><FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                {searchTerm && (
                    <button
                        onClick={clearSearch}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        aria-label="Clear search"
                    ><FaTimes /></button>
                )}
            </div></div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 border border-green-300 rounded">
          {successMessage} {/* Message already includes terminology */}
        </div>
      )}
      {/* Display API Error Message */}
      {apiError && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded">
          Error: {apiError} {/* Message already includes terminology */}
        </div>
      )}
      {/* Display Info Message */}
      {infoMessage && (
        <div className="mb-4 p-3 bg-blue-100 text-blue-700 border border-blue-300 rounded">
          {infoMessage}
        </div>
      )}

      {/* Loading, Error, and Search States */}
      {(loading || isSearching) && <p className="text-center text-gray-500 py-4">{isSearching ? `Searching ${terminology.client.plural.toLowerCase()}...` : `Loading ${terminology.client.plural.toLowerCase()}...`}</p>}
      {error && !apiError && <p className="text-center text-red-500 bg-red-100 p-3 rounded">Error: {error}</p>}
      {!loading && !isSearching && searchTerm.length > 0 && searchTerm.length < 3 && (
          <p className="text-center text-gray-500 p-6">Please enter at least 3 characters to search.</p>
      )}

      {!loading && !error && !(searchTerm.length > 0 && searchTerm.length < 3) && (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-2xl overflow-hidden">
          {clients.length === 0 ? (
            <p className="text-center text-gray-500 p-6">
                {searchTerm ? `No ${terminology.client.plural.toLowerCase()} found matching your search.` : `No ${terminology.client.plural.toLowerCase()} found.`}
            </p>
          ) : (
            <table className="min-w-full leading-normal"><thead><tr className="bg-gray-100 text-left text-gray-600 uppercase text-sm"><th className="px-5 py-3 border-b-2 border-gray-200">Name</th><th className="px-5 py-3 border-b-2 border-gray-200">Email</th><th className="px-5 py-3 border-b-2 border-gray-200">Phone</th><th className="px-5 py-3 border-b-2 border-gray-200">Address</th><th className="px-5 py-3 border-b-2 border-gray-200 text-center whitespace-nowrap min-w-[120px]">Actions</th></tr></thead><tbody>
                {clients.map((client) => (
                  <tr key={client.id} className="hover:bg-gray-50"><td className="px-5 py-4 border-b border-gray-200 bg-white text-sm"><p className="text-gray-900 whitespace-no-wrap">{getDisplayName(client)}</p></td><td className="px-5 py-4 border-b border-gray-200 bg-white text-sm"><p className="text-gray-900 whitespace-no-wrap">{client.email || 'N/A'}</p></td><td className="px-5 py-4 border-b border-gray-200 bg-white text-sm"><p className="text-gray-900 whitespace-no-wrap">{client.phoneNumber || 'N/A'}</p></td><td className="px-5 py-4 border-b border-gray-200 bg-white text-sm"><p className="text-gray-900 whitespace-no-wrap">{client.addressLine1 || 'N/A'}</p></td><td className="px-5 py-4 border-b border-gray-200 bg-white text-sm text-center whitespace-nowrap">{!isDemoMode() && <button onClick={() => handleEdit(client.id)} className="text-green-600 hover:text-green-700 transition-colors mr-3 transition duration-300"><FaEdit /></button>}
                      {/* Pass the whole client object to handleDelete */}
                      {!isDemoMode() && <button onClick={() => handleDelete(client)} className="text-red-500 hover:text-red-700 transition-colors transition duration-300"><FaTrashAlt /></button>}</td></tr>
                ))}
              </tbody></table>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {!loading && !error && !(searchTerm.length > 0 && searchTerm.length < 3) && clients.length > 0 && (hasPreviousPage || hasNextPage) && (
        <div className="mt-6 flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg shadow-sm"><div className="flex items-center"><p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span>
              {searchTerm ? ` - Search results for "${searchTerm}"` : ''}
            </p></div><div className="flex items-center space-x-2"><Button
              onClick={handlePreviousPage}
              disabled={!hasPreviousPage || loading}
              variant="secondary"
            ><FaChevronLeft className="mr-1 h-3 w-3" />
              Previous
            </Button><Button
              onClick={handleNextPage}
              disabled={!hasNextPage || loading}
              variant="secondary"
            >
              Next
              <FaChevronRight className="ml-1 h-3 w-3" /></Button></div></div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDelete}
        title={`Confirm Deletion - ${terminology.client.singular}`} // Use terminology
        message={`Are you sure you want to delete the ${terminology.client.singular.toLowerCase()} "${getDisplayName(clientToDelete || {})}"? This action cannot be undone.`} // Use terminology
      /></div>
  );
};

export default ClientsPage;
